import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Share2, Flame, Loader2, BadgeCheck, Zap } from "lucide-react";
import { entryDays, streakStats, liveORank } from "../../lib/orank";
import { exportORankStory } from "./orankShare";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D4AF37", RED = "#C41E24";
const SUBS = [["dribbling", "Dribbling", "DRI"], ["ball_control", "Ball Control", "CTL"], ["shooting", "Shooting", "SHO"], ["ball_mastery", "Ball Mastery", "MAS"]];

const Sub = ({ label, short, r, accent }) => (
  <div data-testid={`orank-sub-${label.toLowerCase().replace(/\s+/g, "-")}`} className="min-w-0">
    <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.2em] text-white/50"><span>{short}</span>{r?.source === "coach" && <BadgeCheck size={10} style={{ color: accent }} title="Coach-graded" />}</div>
    <div className="font-cover text-2xl leading-none mt-0.5" style={{ color: accent }}>{r?.value ?? "—"}</div>
    <div className="h-1 rounded-full bg-white/10 mt-1.5 overflow-hidden"><motion.div className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${RED}, ${accent})` }} initial={{ width: 0 }} animate={{ width: `${((r?.value ?? 0) / 99) * 100}%` }} transition={{ duration: 0.9, ease: "easeOut" }} /></div>
  </div>
);

// Overall O-Rank medallion — official accreditation look; streak bonus recomputed live from journal entries.
export default function ORankMedallion({ token, player, entries, isElite, onData }) {
  const accent = isElite ? GOLD : RED;
  const [data, setData] = useState(null);
  const [sharing, setSharing] = useState(false);
  const todayStr = new Date().toISOString().slice(0, 10);
  const streak = useMemo(() => streakStats(entryDays(entries), todayStr).cur, [entries, todayStr]);
  const live = useMemo(() => liveORank(data?.ratings, streak), [data, streak]);

  useEffect(() => {
    if (!token || !player?.id) return;
    axios.get(`${API}/orank/me`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data: d }) => setData(d)).catch(() => {});
  }, [token, player?.id]);
  useEffect(() => { if (live && onData) onData({ ...live, ratings: data.ratings }); }, [live, data, onData]);

  const share = async () => {
    if (!live) return;
    setSharing(true);
    try {
      const r = await exportORankStory({ name: player?.display_name || player?.name, tier: isElite ? "Elite" : player?.tier === "academy" ? "Technical Track" : "Blueprint", ...live, ratings: data.ratings });
      if (r === "downloaded") toast.success("O-Rank story saved", { description: "1080×1920 PNG — post it to your Instagram story." });
    } catch (e) { toast.error("Couldn't build the share image."); }
    setSharing(false);
  };

  return (
    <motion.section data-testid="orank-medallion" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className={`relative rounded-xl border backdrop-blur-xl overflow-hidden grain ${isElite ? "elite-carbon-bg" : "bg-[#121212]"}`}
      style={{ borderColor: `${accent}55`, boxShadow: `0 20px 60px rgba(0,0,0,.5), 0 0 48px ${accent}1a, inset 0 1px 0 ${accent}66` }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(260px 200px at 30% 30%, ${accent}2a, transparent 70%)` }} />
      <div className="relative p-5 flex flex-col gap-4 h-full">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.35em]" style={{ color: accent }}><Zap size={12} /> Overall O-Rank</div>
          <button data-testid="orank-share-btn" onClick={share} disabled={sharing || !live} aria-label="Share progress" title="Share Progress"
            className="w-9 h-9 rounded-full border flex items-center justify-center transition-colors hover:text-black disabled:opacity-50" style={{ borderColor: `${accent}66`, color: accent }}
            onMouseEnter={(e) => { e.currentTarget.style.background = accent; }} onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
            {sharing ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />}
          </button>
        </div>
        <div className="flex items-center gap-5">
          <div className="relative shrink-0 w-28 h-28 rounded-full flex items-center justify-center" style={{ border: `3px solid ${accent}`, boxShadow: `0 0 30px ${accent}66, inset 0 0 30px ${accent}22`, background: "radial-gradient(circle at 35% 30%, #1c1c1c, #050505)" }}>
            <motion.span className="absolute inset-[-8px] rounded-full pointer-events-none" style={{ border: `1px solid ${accent}55` }} animate={{ scale: [1, 1.08, 1], opacity: [0.6, 0.15, 0.6] }} transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }} />
            <span data-testid="orank-value" className="inline-flex"><AnimatePresence mode="popLayout" initial={false}>
              <motion.span key={live?.orank ?? "x"} initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 1.3, opacity: 0 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="font-cover text-6xl leading-none" style={{ color: "#fff", textShadow: `0 0 26px ${accent}aa` }}>{live ? live.orank : "—"}</motion.span>
            </AnimatePresence></span>
            <span className="absolute bottom-3 font-mono text-[8px] font-bold uppercase tracking-[0.3em]" style={{ color: accent }}>O-Rank</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-headings text-base uppercase tracking-wide leading-tight">Official Technical<br />Accreditation</div>
            <div data-testid="orank-bonus-chip" className="mt-2 inline-flex items-center gap-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] rounded-full px-2.5 py-1 border"
              style={live?.bonus ? { color: "#0a0a0a", background: accent, borderColor: accent, boxShadow: `0 0 14px ${accent}88` } : { color: "rgba(255,255,255,0.45)", borderColor: "rgba(255,255,255,0.15)" }}>
              <Flame size={10} /> {live?.bonus ? `Streak Bonus +${live.bonus}` : "No streak bonus yet"}
            </div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 mt-1.5">Base {live?.base ?? "—"} · {streak}-day streak</div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-3 border-t" style={{ borderColor: `${accent}22` }}>
          {SUBS.map(([k, label, short]) => <Sub key={k} label={label} short={short} r={data?.ratings?.[k]} accent={accent} />)}
        </div>
      </div>
    </motion.section>
  );
}
